<template>
  <!-- MYAC-1427 — below 1024px the menu folds into its button instead of
       wrapping onto a second row (PrimeVue's own default is 960px). -->
  <Menubar :model="menuItems" breakpoint="1023px" class="!rounded-none">
    <template #start>
      <!-- MYAC-1393 — the square mark on phones: the title logo squashed to an
           unreadable sliver there, and the header must fit in ~320px.
           MYAC-1427 — up to 1279px too: the title logo's 232px is what keeps
           five menu entries from fitting on one row. Its gap stays tight below
           1536px for the same reason (French, with a third dropdown). -->
      <router-link to="/" class="inline-block">
        <img src="@/assets/logo_light.svg" :alt="t('shared.logo_ring')" class="h-8 w-8 !mr-2 xl:hidden" />
        <img src="@/assets/logo_title_light.svg" :alt="t('shared.logo_ring')" class="hidden xl:inline w-[200px] !mr-4 2xl:!mr-8" />
      </router-link>
    </template>
    <template #item="{ item, props, hasSubmenu, root }">
      <template v-if="item.separator">
        <Divider />
      </template>
      <template v-else-if="item.route">
        <router-link v-slot="{ href, navigate }" :to="item.route" custom>
          <a v-ripple :href="href" @click="navigate" v-bind="props.action" v-tooltip.bottom="item.tooltip"
            :style="item.style" class="flex items-center w-full h-full">
            <span>{{ item.label }}</span>
            <i v-if="hasSubmenu"
              :class="['pi pi-angle-down ml-auto', { 'pi-angle-down': root, 'pi-angle-right': !root }]"></i>
          </a>
        </router-link>
      </template>
      <template v-else-if="item.items">
        <a v-ripple v-bind="props.action" v-tooltip.bottom="item.tooltip" :style="item.style"
          class="flex items-center w-full h-full">
          <span>{{ item.label }}</span>
          <i v-if="hasSubmenu"
            :class="['pi pi-angle-down ml-auto', { 'pi-angle-down': root, 'pi-angle-right': !root }]"></i>
        </a>
      </template>
      <template v-else>
        <a v-ripple v-bind="props.action" v-tooltip.bottom="item.tooltip" :style="item.style"
          class="flex items-center w-full h-full">
          <span>{{ item.label }}</span>
        </a>
      </template>
    </template>
    <template #end>
      <div class="flex items-center gap-1 sm:gap-2">
        <TenantSwitcher compact-until-2xl />
        <!-- MYAC-917 — global chat launcher. Opening the panel is free; the paid
             send action stays credit-gated inside ChatInterface ($$$: bypass).

             MYAC-1156 — disabled, not hidden, on the dedicated chat pages: the
             member is already looking at the conversation, so a dock beside it
             would be the same chat twice. A persistent global control that
             vanishes per page reads as a missing feature; one that stays and
             says why teaches the state. The reason rides the wrapper span
             because a disabled PrimeVue button swallows its own tooltip. -->
        <span v-tooltip.bottom="launcherTooltip" class="inline-flex">
          <Button icon="pi pi-sparkles" :aria-label="launcherTooltip" text
            :disabled="chatPanelStore.suppressed"
            :severity="chatPanelStore.isVisible ? 'primary' : undefined" @click="chatPanelStore.toggle()" />
        </span>
        <span v-tooltip.bottom="t('shared.help')" class="inline-flex">
          <Button icon="pi pi-question-circle" :aria-label="t('shared.help')" text @click="openHelp" />
        </span>
        <ButtonWithAdminTooltip :disabled="!isTenantAdmin">
          <span v-if="isTenantAdmin" v-tooltip.bottom="t('shared.settings')" class="inline-flex">
            <LinkButton icon="pi pi-cog" :label="''" :aria-label="t('shared.settings')" text :to="{ path: '/settings' }" />
          </span>
          <span v-else class="inline-flex">
            <LinkButton icon="pi pi-cog" :label="''" :aria-label="t('shared.settings')" text :to="{ path: '/settings' }"
              :disabled="true" />
          </span>
        </ButtonWithAdminTooltip>
        <span v-tooltip.bottom="t('shared.profile')" class="inline-flex">
          <LinkButton icon="pi pi-user" :label="''" :aria-label="t('shared.profile')" text :to="{ name: 'profile' }" />
        </span>
      </div>
    </template>
  </Menubar>
  <ProjectCreateDialog v-model:visible="createDialogVisible" :initialType="ProjectCreationRequestType.QMS" />
  <ProjectCreateDialog v-model:visible="createTdDialogVisible" :initialType="ProjectCreationRequestType.TD" />
  <ProjectCreateDialog v-model:visible="createRegMonitoringDialogVisible" :initialType="ProjectCreationRequestType.REGULATORY_MONITORING" />
  <HelpContactModal v-model:visible="helpDialogVisible" />
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue'
import { useRouter, useRoute, onBeforeRouteUpdate } from 'vue-router'
import { useProjectsStore } from '@/stores/projects'
import ProjectCreateDialog from '@/components/projects/ProjectCreateDialog.vue'
import HelpContactModal from '@/components/shared/HelpContactModal.vue'
import { ProjectCreationRequestType } from '@/models/Billing'
import { useI18n } from 'vue-i18n'
import LinkButton from '@/components/shared/LinkButton.vue'
import ButtonWithAdminTooltip from '@/components/shared/ButtonWithAdminTooltip.vue'
import TenantSwitcher from '@/components/shared/TenantSwitcher.vue'
import { logger } from '@/utils/logger'
import { useFeaturesStore } from '@/stores/features'
import { DASHBOARD_ROUTE, useChatPanelStore } from '@/stores/chatPanel'
import { useSupportChat } from '@/composables/useSupportChat'
import type { MenuItem } from 'primevue/menuitem'

const projectsStore = useProjectsStore()
const router = useRouter()
const route = useRoute()
const featuresStore = useFeaturesStore()
const chatPanelStore = useChatPanelStore()
const supportChat = useSupportChat()

/** MYAC-1156 — on a chat page the launcher is inert, and says so. */
const launcherTooltip = computed(() => !chatPanelStore.suppressed
  ? t('chat.panel-launcher-tooltip')
  : route.name === DASHBOARD_ROUTE ? t('chat.panel-launcher-on-dashboard') : t('chat.panel-launcher-on-chat-page'))
const createDialogVisible = ref(false)
const createTdDialogVisible = ref(false)
const createRegMonitoringDialogVisible = ref(false)
const helpDialogVisible = ref(false)
const { t } = useI18n()

const isTenantAdmin = computed(() => featuresStore.isTenantAdmin)
const canCreateWatch = computed(() =>
  featuresStore.canCreateProjectOfType(ProjectCreationRequestType.REGULATORY_MONITORING))

// ✅ Reactive filters
const byNameAsc = (a: { name: string }, b: { name: string }) =>
  (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' })

const qms = computed(() =>
  projectsStore.tenantProjects.filter((p) => p.type === 'qms').slice().sort(byNameAsc)
)

const td = computed(() =>
  projectsStore.tenantProjects.filter((p) => p.type === 'td').slice().sort(byNameAsc)
)

const regulatoryMonitoring = computed(() =>
  projectsStore.tenantProjects
    .filter((p) => p.type === 'regulatory_monitoring')
    .slice()
    .sort(byNameAsc)
)

// ✅ Make menuItems reactive too
const menuItems = computed(() => {
  const items: MenuItem[] = [
    {
      label: t('menu.dashboard'),
      route: '/dashboard'
    },
    // MYAC-1268 — beside the dashboard, because it answers the same
    // tenant-wide question ("where does my attention go") rather than
    // belonging to any one project.
    {
      label: t('menu.tasks'),
      route: '/tasks'
    },
    {
      label: t('menu.quality_management_system'),
      items: [
        ...qms.value.map((p) => ({
          label: p.name,
          route: `/qms/${p.id}`
        })),
        {
          separator: true
        },
        {
          label: t('menu.create_qms'),
          style: { color: 'var(--p-primary-500)' },
          command: openCreateDialog,
          disabled: !isTenantAdmin.value
        }
      ]
    },
  ]

  items.push({
    label: t('menu.technical_documentation'),
    items: [
      ...td.value.map((p) => ({
        label: p.name,
        route: `/products/${p.id}`
      })),
      {
        separator: true
      },
      {
        label: t('menu.create_td'),
        style: { color: 'var(--p-primary-500)' },
        command: openCreateTdDialog,
        disabled: !isTenantAdmin.value
      }
    ]
  })

  const regMon = regulatoryMonitoring.value
  if (regMon.length === 0) {
    // MYAC-1522 (decision E, as « Créer un QMS ») — with no watch yet the entry is
    // the module's door: the create dialog for an admin whose plan allows a watch,
    // the watch list for everyone else (its empty state says why: not in the plan,
    // or ask an admin). It used to open the create dialog for anyone, a member
    // included. A link, not a command, when it navigates.
    items.push(canCreateWatch.value
      ? { label: t('menu.regulatory_monitoring'), command: openCreateRegMonitoringDialog }
      : { label: t('menu.regulatory_monitoring'), route: '/regulatory-monitoring' })
  } else if (regMon.length === 1) {
    items.push({
      label: t('menu.regulatory_monitoring'),
      route: `/regulatory-monitoring/${regMon[0].id}`,
    })
  } else {
    items.push({
      label: t('menu.regulatory_monitoring'),
      items: [
        ...regMon.map((p) => ({
          label: p.name,
          route: `/regulatory-monitoring/${p.id}`
        })),
        {
          separator: true
        },
        {
          label: t('menu.create_regulatory_monitoring'),
          style: { color: 'var(--p-primary-500)' },
          command: openCreateRegMonitoringDialog,
          disabled: !isTenantAdmin.value
        }
      ]
    })
  }

  return items
})

// ✅ Initial project setting
if (route.params.pid) {
  projectsStore.setCurrentProject(route.params.pid as string)
}

// ✅ Update when route params change
onBeforeRouteUpdate((to, from, next) => {
  if (to.params.pid) {
    projectsStore.setCurrentProject(to.params.pid as string)
  }
  next()
})


// MYAC-1522 (onboarding pass, decision E) — « Créer un QMS » opened a dialog
// titled after the first type the plan allows (« Créer une veille
// réglementaire » on Veille), with the QMS greyed. Now: the dialog when the plan
// allows that type; otherwise the list, whose empty state says it is not in the
// plan and leads to the team (EmptyProjectsState), or, past the quota, the list
// itself with its own reason on « Ajouter ».
function openCreateDialog() {
  if (featuresStore.canCreateProjectOfType(ProjectCreationRequestType.QMS)) createDialogVisible.value = true
  else void router.push('/qms')
}

function openCreateTdDialog() {
  if (featuresStore.canCreateProjectOfType(ProjectCreationRequestType.TD)) createTdDialogVisible.value = true
  else void router.push('/products')
}

function openCreateRegMonitoringDialog() {
  if (canCreateWatch.value) createRegMonitoringDialogVisible.value = true
  else void router.push('/regulatory-monitoring')
}

// MYAC-1348 — the support chat when this environment has it; the contact modal
// otherwise, and also when the chat cannot open (vendor script down), so the
// help button never does nothing.
async function openHelp() {
  if (await supportChat.open()) return
  helpDialogVisible.value = true
}

watch(() => projectsStore.currentProjectId, (newId) => {
  logger.debug("Project ID changed", newId)
})

// No handler needed; dialog calls billing store directly

// MYAC-1208 — load the projects the menus read, and nothing more. This used to
// auto-open the create dialog at zero projects as a first-run nudge, but "no
// project" is also the normal state of someone invited into the company and not
// yet granted one: they got a forced creation flow instead of the dashboard.
// The empty dashboard now carries that invitation (DashboardPage.vue).
onMounted(async () => {
  try {
    if (!projectsStore.loadedOnce) {
      await projectsStore.listProjects(false)
    }
  } catch {
    // best-effort; the menus simply render without project entries
  }
})

</script>

<style scoped>
/* MYAC-1393 — while the square mark shows (below 1280px since MYAC-1427) it
 * holds its size and the switcher squeezes instead. Not wider: there the title
 * logo is what gives way, so the menu items keep their room. */
@media (max-width: 1279px) {
  :deep(.p-menubar-start) {
    flex-shrink: 0;
  }
}

/* MYAC-1418 — « Travail en cours » is ~70px longer than « Tâches »: below
 * 1536px five French entries no longer fit with the default side padding (and
 * the company name, now icon-only there), so it tightens, more so below 1280px
 * where the bar is narrowest. */
@media (min-width: 1024px) and (max-width: 1535px) {
  :deep(.p-menubar-root-list > .p-menubar-item > .p-menubar-item-content > a) {
    padding-inline: 0.5rem;
  }
}
@media (min-width: 1024px) and (max-width: 1279px) {
  :deep(.p-menubar-root-list > .p-menubar-item > .p-menubar-item-content > a) {
    padding-inline: 0.375rem;
  }
}

/* Keep the Menubar submenu above the Tabs active-bar indicator below it. */
:deep(.p-menubar-submenu) {
  z-index: 1100;
}

/* MYAC-1556 — a long project list scrolls inside its dropdown instead of running
 * past the bottom of the window, where its last projects and the create entry
 * could not be reached. 3.625rem is where the dropdown starts under the bar
 * from 1280px (0.5rem higher below that, where the bar is shorter, which only
 * widens the gap), 0.5rem the gap kept above the window's edge. The floor (four
 * 2.5rem rows and the list's padding) is for a very short window: the dropdown
 * keeps that height and the page, which scrolls under this header, brings its
 * end on screen. Desktop only: below 1024px the lists sit inline in the folded
 * menu, which must not become a scroller inside a scroller. */
@media (min-width: 1024px) {
  :deep(.p-menubar-submenu) {
    max-height: max(10.5rem, calc(100dvh - 3.625rem - 0.5rem));
    overflow-y: auto;
  }
}
</style>
